import React from 'react'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { HelpCircle } from 'lucide-react'
import {
    DOCUMENT_GROUPS,
    DOCUMENT_TYPES,
    DOCUMENT_GROUP_TO_TYPES,
    DOCUMENT_TYPE_TO_GROUP,
    DocumentGroup,
    DocumentType,
} from '@/modules/Asset/constants'
import { AssetFormData } from '..'

interface DocumentClassificationSectionProps {
    formData: AssetFormData
    setFormData: React.Dispatch<React.SetStateAction<AssetFormData>>
}

const DocumentClassificationSection: React.FC<
    DocumentClassificationSectionProps
> = ({ formData, setFormData }) => {
    const { t } = useTranslation('asset')

    return (
        <div className="rounded-lg border border-gray-100 p-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-800">
                {t('labels.documentGroup')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="documentGroup">
                        {t('labels.documentGroup')}{' '}
                        <span className="text-destructive">*</span>
                    </Label>
                    <Select
                        value={formData.documentGroup}
                        onValueChange={(value) => {
                            setFormData((prev) => {
                                const validTypes =
                                    DOCUMENT_GROUP_TO_TYPES[
                                        value as DocumentGroup
                                    ] || []
                                const currentType =
                                    prev.documentType as DocumentType
                                const nextType = validTypes.includes(
                                    currentType
                                )
                                    ? currentType
                                    : ''

                                return {
                                    ...prev,
                                    documentGroup: value,
                                    documentType: nextType,
                                    isReadConfirmationRequired:
                                        nextType === 'REGULATION'
                                            ? prev.isReadConfirmationRequired
                                            : false,
                                }
                            })
                        }}
                    >
                        <SelectTrigger>
                            <SelectValue
                                placeholder={t(
                                    'placeholders.selectDocumentGroup'
                                )}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {DOCUMENT_GROUPS.filter(
                                (group) => group.value !== 'PENDING'
                            ).map((group) => (
                                <SelectItem
                                    key={group.value}
                                    value={group.value}
                                >
                                    {t(`document_groups.${group.value}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label htmlFor="documentType">
                        {t('labels.documentType')}{' '}
                        <span className="text-destructive">*</span>
                    </Label>
                    <Select
                        value={formData.documentType}
                        onValueChange={(value) => {
                            setFormData((prev) => {
                                const group =
                                    DOCUMENT_TYPE_TO_GROUP[
                                        value as DocumentType
                                    ]
                                // Auto-set shareScope to 'public' for REGULATION documents
                                const isRegulation = value === 'REGULATION'
                                return {
                                    ...prev,
                                    documentType: value,
                                    documentGroup: group || prev.documentGroup,
                                    isReadConfirmationRequired: isRegulation
                                        ? prev.isReadConfirmationRequired
                                        : false,
                                    // When REGULATION is selected, set share to public and clear private share lists
                                    shareScope: isRegulation
                                        ? 'public'
                                        : prev.shareScope,
                                    shared_employee_ids: isRegulation
                                        ? []
                                        : prev.shared_employee_ids,
                                    shared_department_ids: isRegulation
                                        ? []
                                        : prev.shared_department_ids,
                                }
                            })
                        }}
                    >
                        <SelectTrigger>
                            <SelectValue
                                placeholder={t(
                                    'placeholders.selectDocumentType'
                                )}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {DOCUMENT_TYPES.filter((type) => {
                                if (type.value === 'PENDING') return false
                                if (
                                    formData.documentGroup &&
                                    formData.documentGroup !== 'PENDING'
                                ) {
                                    const allowedTypes =
                                        DOCUMENT_GROUP_TO_TYPES[
                                            formData.documentGroup as DocumentGroup
                                        ]
                                    return allowedTypes
                                        ? allowedTypes.includes(
                                              type.value as DocumentType
                                          )
                                        : true
                                }
                                return true
                            }).map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                    {t(`document_types.${type.value}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {formData.documentType === 'REGULATION' && (
                <div className="flex items-center space-x-2 mt-4">
                    <Checkbox
                        id="readConfirmation"
                        checked={formData.isReadConfirmationRequired}
                        onCheckedChange={(checked) =>
                            setFormData((prev) => ({
                                ...prev,
                                isReadConfirmationRequired: checked as boolean,
                            }))
                        }
                    />
                    <div className="flex items-center space-x-2">
                        <Label
                            htmlFor="readConfirmation"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                            {t('form.readConfirmationRequired')}
                        </Label>
                        <TooltipProvider>
                            <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                    <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="max-w-xs">
                                        {t('form.readConfirmationTooltip')}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>
            )}

            {/* Applies to every document type, unlike the read-confirmation option above. */}
            <div className="flex items-center space-x-2 mt-4">
                <Checkbox
                    id="downloadRestricted"
                    checked={formData.isDownloadRestricted}
                    onCheckedChange={(checked) =>
                        setFormData((prev) => ({
                            ...prev,
                            isDownloadRestricted: checked as boolean,
                        }))
                    }
                />
                <div className="flex items-center space-x-2">
                    <Label
                        htmlFor="downloadRestricted"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                        {t('form.downloadRestricted')}
                    </Label>
                    <TooltipProvider>
                        <Tooltip delayDuration={300}>
                            <TooltipTrigger asChild>
                                <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                                <p className="max-w-xs">
                                    {t('form.downloadRestrictedTooltip')}
                                </p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>
        </div>
    )
}

export default DocumentClassificationSection
